import { TrendingUp, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const LAST_UPDATED = 'February 20, 2026';

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 text-white">{title}</h2>
      <div className="space-y-4 text-gray-300 leading-relaxed">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 pl-5 list-disc marker:text-blue-500/70">
      {items.map((item, i) => (
        <li key={i} className="text-gray-300">{item}</li>
      ))}
    </ul>
  );
}

export default function Privacy() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0c]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="privacy-logo-home"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">Trading Pilot <span className="text-blue-500">360</span></span>
            </div>
            <nav className="flex items-center space-x-6">
              <button
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                data-testid="privacy-nav-home"
              >
                {user ? 'Dashboard' : 'Home'}
              </button>
              {!user && (
                <Button
                  onClick={() => navigate('/?login=1')}
                  variant="outline"
                  className="border-white/20 hover:bg-white/10"
                  data-testid="privacy-nav-login"
                >
                  Login
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-blue-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-sm text-gray-500 mb-10">Last updated: {LAST_UPDATED}</p>

          <div className="space-y-4 text-gray-300 leading-relaxed mb-10">
            <p>
              Trading Pilot 360 (“we”, “our”, or “the platform”) provides an AI-assisted trading
              decision support tool that integrates with TradingView alerts and provides paper
              trading automation and performance analytics.
            </p>
            <p>We respect your privacy and are committed to protecting your personal data.</p>
          </div>

          <Section title="Information we collect">
            <p>We may collect:</p>
            <BulletList items={[
              'email address (when joining waitlist or creating account)',
              'name (optional)',
              'usage data (pages visited, features used)',
              'technical data (browser type, device type)',
              'IP address (hashed for visitor analytics)',
              'TradingView webhook signal data (symbol, price, timeframe)',
            ]} />
            <p className="pt-2">We do NOT collect:</p>
            <BulletList items={[
              'broker login credentials',
              'financial account details',
              'payment information (until subscriptions are introduced)',
            ]} />
          </Section>

          <Section title="How we use information">
            <p>We use collected data to:</p>
            <BulletList items={[
              'provide access to the platform',
              'operate signal processing features',
              'improve product functionality',
              'send confirmation emails',
              'send product updates',
              'analyse usage patterns',
              'improve system performance',
            ]} />
          </Section>

          <Section title="Google Analytics">
            <p>We use Google Analytics 4 to understand website usage and improve the platform.</p>
            <p>Google Analytics collects anonymised usage data such as:</p>
            <BulletList items={[
              'pages visited',
              'time spent on pages',
              'device information',
              'approximate location',
            ]} />
            <p>No personally identifiable financial data is shared with Google Analytics.</p>
          </Section>

          <Section title="Email communication">
            <p>We may send emails:</p>
            <BulletList items={[
              'waitlist confirmation emails',
              'account notifications',
              'password reset emails',
              'product updates (only if relevant)',
            ]} />
            <p>Users can unsubscribe from marketing emails at any time.</p>
          </Section>

          <Section title="Data storage">
            <p>User data is stored securely using industry standard practices.</p>
            <p>We take reasonable steps to protect data from unauthorised access.</p>
          </Section>

          <Section title="Cookies">
            <p>We may use cookies to:</p>
            <BulletList items={[
              'maintain login sessions',
              'remember preferences',
              'analyse usage patterns',
            ]} />
            <p>Cookies can be disabled in browser settings.</p>
          </Section>

          <Section title="Third-party services">
            <p>We may use trusted third-party providers:</p>
            <BulletList items={[
              'Google Analytics (analytics)',
              'Resend or email provider (transactional emails)',
              'Emergent hosting infrastructure',
            ]} />
            <p>These providers process data on our behalf.</p>
          </Section>

          <Section title="User rights">
            <p>Users may request:</p>
            <BulletList items={[
              'access to stored personal data',
              'correction of incorrect data',
              'deletion of personal data',
            ]} />
            <p>
              Requests can be sent to:{' '}
              <a href="mailto:contact@tradingpilot360.com" className="text-blue-400 hover:text-blue-300">
                contact@tradingpilot360.com
              </a>
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>We may update this Privacy Policy periodically.</p>
            <p>Updates will be posted on this page.</p>
          </Section>

          <Section title="Contact">
            <p>
              For questions about this policy:{' '}
              <a href="mailto:contact@tradingpilot360.com" className="text-blue-400 hover:text-blue-300">
                contact@tradingpilot360.com
              </a>
            </p>
          </Section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold">Trading Pilot <span className="text-blue-500">360</span></span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              <Link to="/about" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-about-link">About</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/disclaimer" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-disclaimer-link">Disclaimer</Link>
              <Link to="/refund-policy" className="text-gray-400 hover:text-white transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Trading Pilot 360. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
